import React, { useState, useEffect, FormEvent } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { publicApi, AvailablePlan } from "../services/api";
import WarningIcon from "@mui/icons-material/Warning";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import BusinessIcon from "@mui/icons-material/Business";
import CheckIcon from "@mui/icons-material/Check";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ReplayIcon from "@mui/icons-material/Replay";
import LoginIcon from "@mui/icons-material/Login";
import AppLoader from "./AppLoader";
import "./SignUpPage.css";

type BillingCycle = "monthly" | "yearly";
type SignUpStep = "plan" | "account" | "checkout";

interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  domain: string;
  planId: string;
  billingCycle: BillingCycle;
}

interface FormErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  companyName?: string;
  domain?: string;
  planId?: string;
}

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check for pre-selected plan from URL or canceled checkout
  const preSelectedPlan = searchParams.get("plan");
  const wasCanceled = searchParams.get("canceled") === "true";
  
  // Form state
  const [currentStep, setCurrentStep] = useState<SignUpStep>("plan");
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
    confirmPassword: "",
    companyName: "",
    domain: "",
    planId: preSelectedPlan || "",
    billingCycle: "monthly",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [apiError, setApiError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Plans state
  const [plans, setPlans] = useState<AvailablePlan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [plansError, setPlansError] = useState("");
  const [trialPeriodDays, setTrialPeriodDays] = useState(0);

  // Load plans on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoadingPlans(true);
        setPlansError("");
        const response = await publicApi.getPlans();
        setPlans(response.plans);
        setTrialPeriodDays(response.trial_period_days || 0);
        
        // Auto-select first plan if none selected
        if (!formData.planId && response.plans.length > 0) {
          setFormData(prev => ({ ...prev, planId: response.plans[0].id }));
        }
      } catch (err) {
        setPlansError(err instanceof Error ? err.message : "Failed to load plans");
      } finally {
        setIsLoadingPlans(false);
      }
    };
    
    fetchPlans();
  }, []);

  // If pre-selected plan exists, skip to account step
  useEffect(() => {
    if (preSelectedPlan && plans.some(p => p.id === preSelectedPlan)) {
      setCurrentStep("account");
    }
  }, [preSelectedPlan, plans]);

  // Validation functions
  const validateEmail = (email: string): string | undefined => {
    if (!email) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email";
    return undefined;
  };

  const validatePassword = (password: string): string | undefined => {
    if (!password) return "Password is required";
    if (password.length < 8) return "Password must be at least 8 characters";
    return undefined;
  };

  const validateConfirmPassword = (confirmPassword: string, password: string): string | undefined => {
    if (!confirmPassword) return "Please confirm your password";
    if (confirmPassword !== password) return "Passwords do not match";
    return undefined;
  };

  const validateCompanyName = (name: string): string | undefined => {
    if (!name) return "Company name is required";
    if (name.trim().length < 2) return "Company name must be at least 2 characters";
    if (name.trim().length > 100) return "Company name must be at most 100 characters";
    return undefined;
  };

  const validatePlan = (): string | undefined => {
    if (!formData.planId) return "Please select a plan";
    return undefined;
  };

  // Validate current step
  const validateStep = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (currentStep === "plan") {
      const planError = validatePlan();
      if (planError) newErrors.planId = planError;
    } else if (currentStep === "account") {
      const emailError = validateEmail(formData.email);
      const passwordError = validatePassword(formData.password);
      const confirmPasswordError = validateConfirmPassword(formData.confirmPassword, formData.password);
      const companyNameError = validateCompanyName(formData.companyName);
      
      if (emailError) newErrors.email = emailError;
      if (passwordError) newErrors.password = passwordError;
      if (confirmPasswordError) newErrors.confirmPassword = confirmPasswordError;
      if (companyNameError) newErrors.companyName = companyNameError;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle step navigation
  const handleNextStep = () => {
    if (!validateStep()) return;
    
    if (currentStep === "plan") {
      setCurrentStep("account");
    } else if (currentStep === "account") {
      handleSubmit();
    }
  };

  const handlePrevStep = () => {
    if (currentStep === "account") {
      setCurrentStep("plan");
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setIsSubmitting(true);
    setApiError("");
    
    try {
      const response = await publicApi.signup({
        email: formData.email,
        password: formData.password,
        company_name: formData.companyName.trim(),
        plan_id: formData.planId,
        billing_cycle: formData.billingCycle,
        ...(formData.domain.trim() && { domain: formData.domain.trim() }),
      });
      
      // Redirect to Stripe Checkout
      setCurrentStep("checkout");
      window.location.href = response.checkout_url;
    } catch (err) {
      setApiError(err instanceof Error ? err.message : "An error occurred during signup");
      setIsSubmitting(false);
    }
  };

  // Format price
  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatCentsPrice = (cents: number) => {
    return formatPrice(cents / 100);
  };

  // Get plan price based on billing cycle
  const getPlanPrice = (plan: AvailablePlan): string => {
    if (formData.billingCycle === "monthly") {
      if (plan.stripe_monthly_price) {
        return formatCentsPrice(plan.stripe_monthly_price.amount);
      }
      return formatPrice(plan.price);
    } else {
      if (plan.stripe_yearly_price) {
        return formatCentsPrice(plan.stripe_yearly_price.amount);
      }
      return formatPrice(plan.yearly_price);
    }
  };

  // Check if plan has pricing for selected cycle
  const hasPriceForCycle = (plan: AvailablePlan): boolean => {
    if (formData.billingCycle === "monthly") {
      return !!(plan.stripe_monthly_price || plan.price > 0);
    }
    return !!(plan.stripe_yearly_price || plan.yearly_price > 0);
  };

  // Calculate yearly savings
  const getYearlySavings = (plan: AvailablePlan): string | null => {
    const monthlyAmount = plan.stripe_monthly_price?.amount || (plan.price * 100);
    const yearlyAmount = plan.stripe_yearly_price?.amount || (plan.yearly_price * 100);
    
    if (!monthlyAmount || !yearlyAmount || yearlyAmount <= 0) return null;
    
    const annualMonthly = monthlyAmount * 12;
    const savings = annualMonthly - yearlyAmount;
    
    if (savings <= 0) return null;
    
    const savingsPercent = Math.round((savings / annualMonthly) * 100);
    return `Save ${savingsPercent}%`;
  };

  // Get selected plan
  const selectedPlan = plans.find(p => p.id === formData.planId);

  // Password strength indicator
  const getPasswordStrength = (password: string): { level: string; color: string } => {
    if (!password) return { level: "", color: "" };
    if (password.length < 8) return { level: "Weak", color: "#ef4444" };
    if (password.length < 12) return { level: "Fair", color: "#f59e0b" };
    if (/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      return { level: "Strong", color: "#10b981" };
    }
    return { level: "Good", color: "#3b82f6" };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  // Render loading state
  if (isLoadingPlans) {
    return <AppLoader message="Loading plans..." />;
  }

  // Render error state
  if (plansError) {
    return (
      <div className="signup-page">
        <div className="signup-background">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>
        <div className="signup-error">
          <WarningIcon className="error-icon" />
          <p>{plansError}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <div className="signup-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="signup-container">
        {/* Header */}
        <div className="signup-header">
          <div className="signup-logo">
            <img src="/clusterview-logo.svg" alt="ClusterView" className="logo-icon" style={{ width: 32, height: 32 }} />
          </div>
          <h1>ClusterView</h1>
          <p className="signup-subtitle">
            {currentStep === "plan" && (trialPeriodDays > 0
              ? `Start your ${trialPeriodDays}-day free trial`
              : "Choose the plan that's right for you")}
            {currentStep === "account" && "Create your account"}
            {currentStep === "checkout" && "Redirecting to checkout..."}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="signup-progress">
          <div className={`progress-step ${currentStep === "plan" ? "active" : ""} ${currentStep === "account" || currentStep === "checkout" ? "completed" : ""}`}>
            <span className="step-number">1</span>
            <span className="step-label">Choose Plan</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${currentStep === "account" ? "active" : ""} ${currentStep === "checkout" ? "completed" : ""}`}>
            <span className="step-number">2</span>
            <span className="step-label">Account</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${currentStep === "checkout" ? "active" : ""}`}>
            <span className="step-number">3</span>
            <span className="step-label">Payment</span>
          </div>
        </div>

        {/* Canceled checkout — offer to restart signup */}
        {wasCanceled && currentStep === "plan" && (
          <div className="signup-step canceled-step">
            <div className="canceled-card">
              <div className="canceled-card-icon">
                <ReplayIcon sx={{ fontSize: 40 }} />
              </div>
              <h2 className="canceled-card-title">Checkout canceled</h2>
              <p className="canceled-card-description">
                No charges were made. You can pick a plan and try again whenever
                you're ready.
              </p>
              <div className="canceled-card-actions">
                <button
                  className="canceled-card-btn primary"
                  onClick={() => {
                    const params = new URLSearchParams(window.location.search);
                    params.delete("canceled");
                    const qs = params.toString();
                    navigate(`/signup${qs ? `?${qs}` : ""}`, { replace: true });
                    window.location.reload();
                  }}
                >
                  <ReplayIcon fontSize="small" />
                  Choose a plan
                </button>
                <button
                  className="canceled-card-btn secondary"
                  onClick={() => navigate("/login")}
                >
                  <LoginIcon fontSize="small" />
                  Back to login
                </button>
              </div>
            </div>
          </div>
        )}

        {/* API Error */}
        {apiError && (
          <div className="signup-api-error">
            <WarningIcon className="error-icon" fontSize="small" />
            {apiError}
          </div>
        )}

        {/* Step 1: Plan Selection */}
        {!wasCanceled && currentStep === "plan" && (
          <div className="signup-step plan-step">
            {/* Billing Cycle Toggle */}
            {/* <div className="billing-cycle-toggle">
              <button
                className={`billing-cycle-btn ${formData.billingCycle === "monthly" ? "active" : ""}`}
                onClick={() => setFormData(prev => ({ ...prev, billingCycle: "monthly" }))}
                type="button"
              >
                Monthly
              </button>
              <button
                className={`billing-cycle-btn ${formData.billingCycle === "yearly" ? "active" : ""}`}
                onClick={() => setFormData(prev => ({ ...prev, billingCycle: "yearly" }))}
                type="button"
              >
                Yearly
                <span className="billing-cycle-badge">Save up to 20%</span>
              </button>
            </div> */}

            {/* Plans Grid */}
            <div className="plans-grid mt-8">
              {plans.map((plan) => {
                const isSelected = formData.planId === plan.id;
                const yearlySavings = formData.billingCycle === "yearly" ? getYearlySavings(plan) : null;
                const hasPrice = hasPriceForCycle(plan);
                
                return (
                  <div
                    key={plan.id}
                    className={`plan-card ${isSelected ? "selected" : ""} ${!hasPrice ? "disabled" : ""}`}
                    onClick={() => hasPrice && setFormData(prev => ({ ...prev, planId: plan.id }))}
                  >
                    {yearlySavings && (
                      <div className="plan-savings-badge">{yearlySavings}</div>
                    )}
                    
                    <div className="plan-header">
                      <h3 className="plan-name">{plan.name}</h3>
                      <div className="plan-price">
                        <span className="plan-price-amount">{getPlanPrice(plan)}</span>
                        <span className="plan-price-period">
                          /{formData.billingCycle === "monthly" ? "month" : "year"}
                        </span>
                      </div>
                      {formData.billingCycle === "yearly" && plan.stripe_yearly_price && (
                        <p className="plan-monthly-equiv">
                          {formatCentsPrice(plan.stripe_yearly_price.amount / 12)}/month billed annually
                        </p>
                      )}
                    </div>

                    <ul className="plan-features">
                      <li>
                        <CheckIcon style={{ fontSize: 16 }} />
                        {plan.max_domains} Domain{plan.max_domains !== 1 ? "s" : ""}
                      </li>
                      <li>
                        <CheckIcon style={{ fontSize: 16 }} />
                        {plan.max_clusters} Keyword Clusters
                      </li>
                      <li>
                        <CheckIcon style={{ fontSize: 16 }} />
                        {plan.max_keywords_per_cluster} Keywords per Cluster
                      </li>
                      <li>
                        <CheckIcon style={{ fontSize: 16 }} />
                        {plan.max_users} Team Member{plan.max_users !== 1 ? "s" : ""}
                      </li>
                    </ul>

                    {isSelected && (
                      <div className="plan-selected-indicator">
                        <CheckIcon />
                        Selected
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Enterprise Plan - Contact Us */}
              <div className="plan-card enterprise-card">
                <div className="enterprise-card-badge">Enterprise</div>
                <div className="plan-header">
                  <h3 className="plan-name">Enterprise</h3>
                  <div className="plan-price">
                    <span className="plan-price-amount">Custom</span>
                  </div>
                </div>

                <ul className="plan-features">  
                  <li>
                    <CheckIcon style={{ fontSize: 16 }} />
                    Unlimited Domains
                  </li>
                  <li>
                    <CheckIcon style={{ fontSize: 16 }} />
                    Unlimited Keyword Clusters
                  </li>
                  <li>
                    <CheckIcon style={{ fontSize: 16 }} />
                    Unlimited Keywords per Cluster
                  </li>
                  <li>
                    <CheckIcon style={{ fontSize: 16 }} />
                    Unlimited Team Members
                  </li>
                  <li>
                    <CheckIcon style={{ fontSize: 16 }} />
                    Dedicated Account Manager
                  </li>
                  <li>
                    <CheckIcon style={{ fontSize: 16 }} />
                    Priority Support & SLA
                  </li>
                </ul>

                <button
                  type="button"
                  className="enterprise-contact-btn"
                  onClick={() => window.location.href = "https://clusterview.ai/contact"}
                >
                  Contact Us
                </button>
              </div>
            </div>

            {errors.planId && (
              <div className="signup-field-error">{errors.planId}</div>
            )}

            <div className="signup-actions">
              <Link to="/login" className="signup-back-link">
                <ArrowBackIcon fontSize="small" />
                Back to Login
              </Link>
              <button
                type="button"
                className="signup-next-btn"
                onClick={handleNextStep}
              >
                Continue
                <ArrowForwardIcon fontSize="small" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Account Details */}
        {currentStep === "account" && (
          <div className="signup-step account-step">
            {/* Selected Plan Summary */}
            {selectedPlan && (
              <div className="selected-plan-summary">
                <span className="summary-label">Selected Plan:</span>
                <span className="summary-value">
                  {selectedPlan.name} - {getPlanPrice(selectedPlan)}/{formData.billingCycle === "monthly" ? "mo" : "yr"}
                </span>
              </div>
            )}

            <form onSubmit={(e: FormEvent) => { e.preventDefault(); handleNextStep(); }} className="signup-form">
              {/* Company Name */}
              <div className="form-group">
                <label htmlFor="companyName">Company Name</label>
                <div className={`input-wrapper ${errors.companyName ? "error" : ""}`}>
                  <BusinessIcon className="input-icon" fontSize="small" />
                  <input
                    id="companyName"
                    type="text"
                    value={formData.companyName}
                    onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                    placeholder="Acme Inc"
                    disabled={isSubmitting}
                  />
                </div>
                {errors.companyName && <span className="field-error">{errors.companyName}</span>}
              </div>

              {/* Website Domain */}
              <div className="form-group">
                <label htmlFor="domain">Website Domain <span className="optional-label">(optional)</span></label>
                <div className={`input-wrapper ${errors.domain ? "error" : ""}`}>
                  <BusinessIcon className="input-icon" fontSize="small" />
                  <input
                    id="domain"
                    type="text"
                    value={formData.domain}
                    onChange={(e) => setFormData(prev => ({ ...prev, domain: e.target.value }))}
                    placeholder="example.com"
                    disabled={isSubmitting}
                  />
                </div>
                <span className="field-hint">
                  Enter your website domain to track your search rankings. You can add or change this later.
                </span>
                {errors.domain && <span className="field-error">{errors.domain}</span>}
              </div>

              {/* Email */}
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <div className={`input-wrapper ${errors.email ? "error" : ""}`}>
                  <EmailIcon className="input-icon" fontSize="small" />
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="you@company.com"
                    autoComplete="email"
                    disabled={isSubmitting}
                  />
                </div>
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>

              {/* Password */}
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className={`input-wrapper ${errors.password ? "error" : ""}`}>
                  <LockIcon className="input-icon" fontSize="small" />
                  <input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    disabled={isSubmitting}
                  />
                </div>
                {formData.password && (
                  <div className="password-strength">
                    <div className="strength-bar">
                      <div 
                        className="strength-fill"
                        style={{ 
                          width: passwordStrength.level === "Weak" ? "25%" : 
                                 passwordStrength.level === "Fair" ? "50%" :
                                 passwordStrength.level === "Good" ? "75%" :
                                 passwordStrength.level === "Strong" ? "100%" : "0%",
                          backgroundColor: passwordStrength.color 
                        }}
                      ></div>
                    </div>
                    <span className="strength-label" style={{ color: passwordStrength.color }}>
                      {passwordStrength.level}
                    </span>
                  </div>
                )}
                {errors.password && <span className="field-error">{errors.password}</span>}
              </div>

              {/* Confirm Password */}
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className={`input-wrapper ${errors.confirmPassword ? "error" : ""}`}>
                  <LockIcon className="input-icon" fontSize="small" />
                  <input
                    id="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    disabled={isSubmitting}
                  />
                </div>
                {errors.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
              </div>

              <div className="signup-actions">
                <button
                  type="button"
                  className="signup-back-btn"
                  onClick={handlePrevStep}
                  disabled={isSubmitting}
                >
                  <ArrowBackIcon fontSize="small" />
                  Back
                </button>
                <button
                  type="submit"
                  className="signup-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="button-spinner"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      {trialPeriodDays > 0 ? "Start Free Trial" : "Continue to Payment"}
                      <ArrowForwardIcon fontSize="small" />
                    </>
                  )}
                </button>
              </div>
            </form>

            <p className="signup-terms">
              By signing up, you agree to our Terms of Service and Privacy Policy.
              {trialPeriodDays > 0 && (
                <> Credit card required. You will not be charged until your {trialPeriodDays}-day trial ends.</>
              )}
            </p>
          </div>
        )}

        {/* Step 3: Checkout Redirect */}
        {currentStep === "checkout" && (
          <div className="signup-step checkout-step">
            <div className="checkout-loading">
              <div className="signup-loading-spinner"></div>
              <p>
                {trialPeriodDays > 0
                  ? "Redirecting to set up your free trial..."
                  : "Redirecting to secure checkout..."}
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="signup-footer">
          <p>
            Already have an account? <Link to="/login">Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
